<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center" class="mt-4 ml-4">
        <v-col>
          <span
            class="text-h5 font-weight-bold"
            v-text="'Monthly Attendances'"
          ></span>
        </v-col>
      </v-row>

      <!-- Filters -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" class="py-4">
            <v-card-text class="pb-0">
              <!-- Row 1: Search, Employee, Company, Role -->
              <v-row dense align="center">
                <v-col md="3" sm="6">
                  <v-text-field
                    v-model="search_keyword"
                    label="Search"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    clearable
                    @keydown.enter="getData"
                    @change="page = 1"
                  ></v-text-field>
                </v-col>
                <v-col md="3" sm="6">
                  <v-autocomplete
                    ref="employee"
                    v-model="filter.user_id"
                    :items="employees"
                    label="Employee"
                    @input="getData"
                    prepend-inner-icon="mdi-account"
                    outlined
                    dense
                    hide-details
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="3" sm="6" v-if="companies.length > 0">
                  <v-autocomplete
                    v-model="filter.filter_company_id"
                    :items="companies"
                    label="Company"
                    @input="onCompanyChange"
                    prepend-inner-icon="mdi-domain"
                    outlined
                    dense
                    hide-details
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="3" sm="6">
                  <v-select
                    v-model="filter.role"
                    :items="roles"
                    label="Role"
                    @change="getData"
                    prepend-inner-icon="mdi-shield-account"
                    outlined
                    dense
                    hide-details
                    clearable
                  ></v-select>
                </v-col>
              </v-row>
              <!-- Row 2: Plant, Salary Cycle, Cycle Month, Start Date, End Date -->
              <v-row dense align="center" class="mt-2">
                <v-col md="2" sm="6">
                  <v-autocomplete
                    ref="plant"
                    v-model="filter.plant_id"
                    :items="plants"
                    label="Plant"
                    @input="getData"
                    prepend-inner-icon="mdi-factory"
                    outlined
                    dense
                    hide-details
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6">
                  <v-autocomplete
                    v-model="filter.salary_cycle_id"
                    :items="salaryCycles"
                    label="Salary Cycle"
                    @input="onSalaryCycleChange"
                    prepend-inner-icon="mdi-cash-clock"
                    outlined
                    dense
                    hide-details
                    clearable
                  ></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6" v-if="filter.salary_cycle_id">
                  <v-menu
                    v-model="cycle_month_menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.cycle_month"
                        label="Cycle Month"
                        prepend-inner-icon="mdi-calendar-month"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        hide-details
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.cycle_month"
                      type="month"
                      @change="(cycle_month_menu = false), onSalaryCycleChange()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col md="2" sm="6">
                  <v-menu
                    v-model="start_date_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :disabled="!!filter.salary_cycle_id"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.start_date"
                        label="Start Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        hide-details
                        :disabled="!!filter.salary_cycle_id"
                        :clearable="!filter.salary_cycle_id"
                        @click:clear="filter.start_date = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.start_date"
                      @change="(start_date_menu = false), getData()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col md="2" sm="6">
                  <v-menu
                    v-model="end_date_menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                    :disabled="!!filter.salary_cycle_id"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="filter.end_date"
                        label="End Date"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        outlined
                        dense
                        hide-details
                        :disabled="!!filter.salary_cycle_id"
                        :clearable="!filter.salary_cycle_id"
                        @click:clear="filter.end_date = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="filter.end_date"
                      @change="(end_date_menu = false), getData()"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
              <!-- Row 3: Pagination -->
              <v-row dense align="center" class="mt-2 mb-1">
                <v-col md="4" sm="12">
                  <v-pagination v-model="page" :length="paginationLength" circle :total-visible="5">
                  </v-pagination>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <!-- / Filters -->

      <!-- Legend -->
      <v-row class="mt-2 mb-2">
        <v-col md="12">
          <v-card elevation="2" class="pa-3">
            <v-card-subtitle class="pb-1 font-weight-bold">Legend:</v-card-subtitle>
            <v-row dense align="center">
              <v-col cols="auto">
                <v-chip small color="green" text-color="white" class="mr-1">P</v-chip>
                <span class="caption">Present</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="red" text-color="white" class="mr-1">A</v-chip>
                <span class="caption">Absent</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="blue" text-color="white" class="mr-1">L</v-chip>
                <span class="caption">Leave</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="purple" text-color="white" class="mr-1">M</v-chip>
                <span class="caption">Meeting</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="grey" text-color="white" class="mr-1">W</v-chip>
                <span class="caption">Weekly Off</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="teal" text-color="white" class="mr-1">H</v-chip>
                <span class="caption">Holiday</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small outlined class="mr-1">-</v-chip>
                <span class="caption">No Record</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="orange" text-color="white" class="mr-1">Late</v-chip>
                <span class="caption">Late Arrival</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="amber darken-2" text-color="white" class="mr-1">HD</v-chip>
                <span class="caption">Half Day (4-6 late marks)</span>
              </v-col>
              <v-col cols="auto">
                <v-chip small color="red" text-color="white" class="mr-1">Absent (7+)</v-chip>
                <span class="caption">Absent Penalty (7+ late marks)</span>
              </v-col>
              <v-col cols="auto">
                <v-icon small color="blue-grey darken-2" class="mr-1">mdi-pencil-box</v-icon>
                <span class="caption">Regularisation (attendance corrected)</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <!-- / Legend -->

      <v-progress-linear
        v-if="isLoading"
        indeterminate
        color="primary"
      ></v-progress-linear>
      <v-row v-else>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-title v-if="monthlyAttendances.length != 0"
              ><v-spacer></v-spacer
              ><v-btn
                class="primary mx-4"
                :loading="isExcelLoading"
                @click="addToExcelData()"
                v-if="!isExcelDownloaded"
                >Generate Excel</v-btn
              >
              <v-btn v-else class="success mx-4" disabled
                ><v-icon left>mdi-check-circle</v-icon>Downloaded</v-btn
              >
              <download-excel
                ref="excelDownload"
                :title="title"
                :data="ExportMonthlyAttendances"
                :fields="excel_fields"
                worksheet="MonthlyAttendances"
                name="MonthlyAttendances.xls"
                style="display: none"
              ></download-excel></v-card-title
            >
            <v-simple-table fixed-header height="65vh" class="monthly-attendance-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-start">Sr No</th>
                    <th class="text-start" style="min-width: 150px">
                      Employee Code
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Employee Name
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Date of joining
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Designation
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Supervisor
                    </th>
                    <th v-for="d in dateRange" class="text-center" :key="`days${d.key}`" style="min-width: 50px;">
                      {{ d.label }}
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Present count
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Absent count
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Leave count
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Meeting count
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Holiday count
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Late Mark
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Half Day
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Excess Break
                    </th>
                    <th class="text-start" style="min-width: 150px">
                      Total Hours
                    </th>
                  </tr>
                </thead>
                <tbody v-if="monthlyAttendances.length != 0">
                  <template v-for="(user, i) in monthlyAttendances">
                    <!-- Main attendance row -->
                    <tr :key="`user${i}`" class="emp-first-row">
                      <td style="min-width: 85px" rowspan="3">
                        <v-tooltip bottom v-if="user.active == 1">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              dark
                              color="green"
                            >
                              mdi-circle
                            </v-icon>
                          </template>
                          <span>User is Active</span>
                        </v-tooltip>
                        <v-tooltip bottom v-if="user.active == 0">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                              small
                              dark
                              color="red"
                            >
                              mdi-circle
                            </v-icon>
                          </template>
                          <span>User is InActive</span>
                        </v-tooltip>
                        &nbsp;
                        {{ i + 1 }}
                      </td>
                      <td rowspan="3">{{ user.employee_code || "" }}</td>
                      <td rowspan="3">{{ user.name || "" }}</td>
                      <td rowspan="3">{{ user.doj || "" }}</td>
                      <td rowspan="3">
                        {{
                          (user.designation && user.designation.description) ||
                          ""
                        }}
                      </td>
                      <td rowspan="3">
                        {{ (user.supervisor && user.supervisor.name) || "" }}
                      </td>

                      <td
                        v-for="d in dateRange"
                        :key="`days${d.key}`"
                        class="day-cell"
                        style="text-align: center; font-weight: bold; padding: 8px 4px; min-width: 80px;"
                      >
                        <!-- Multiple shifts support - Improved UI -->
                        <div v-if="user.attendances[d.key] && user.attendances[d.key].length > 0">
                          <!-- Single shift -->
                          <div v-if="user.attendances[d.key].length === 1">
                            <v-chip
                              small
                              :color="getStatusColor(user.attendances[d.key][0].attendance_status)"
                              text-color="white"
                            >
                              <v-icon v-if="user.attendances[d.key][0].attendance_type === 'Regularisation'" x-small left>mdi-pencil-box</v-icon>
                              {{
                                user.attendances[d.key][0].session_type
                                  ? user.attendances[d.key][0].session_type.description.charAt(0).toUpperCase()
                                  : "A"
                              }}
                            </v-chip>
                            <!-- Late indicator -->
                            <div v-if="user.attendances[d.key][0].is_late" style="font-size: 9px; color: orange; margin-top: 2px">
                              <v-icon x-small color="orange">mdi-clock-alert</v-icon>
                              {{ user.attendances[d.key][0].late_minutes }}m
                            </div>
                          </div>

                          <!-- Multiple shifts - side by side -->
                          <div v-else style="display: flex; gap: 4px; justify-content: center; flex-wrap: wrap;">
                            <div
                              v-for="(att, idx) in user.attendances[d.key]"
                              :key="`att-${d.key}-${idx}`"
                              style="text-align: center;"
                            >
                              <v-chip
                                x-small
                                :color="getStatusColor(att.attendance_status)"
                                text-color="white"
                                label
                              >
                                <v-icon x-small left v-if="att.attendance_type === 'Regularisation'">mdi-pencil-box</v-icon>
                                <v-icon x-small left v-else-if="idx === 0">mdi-weather-sunny</v-icon>
                                <v-icon x-small left v-else>mdi-weather-night</v-icon>
                                {{
                                  att.session_type
                                    ? att.session_type.description.charAt(0).toUpperCase()
                                    : "A"
                                }}
                              </v-chip>
                              <div v-if="att.is_late" style="font-size: 8px; color: orange;">
                                <v-icon x-small color="orange">mdi-clock-alert</v-icon>
                              </div>
                            </div>
                          </div>
                        </div>
                        <span v-else style="color: #999">
                          <span
                            v-if="
                              !user.doj ||
                              (user.doj && d.key >= user.doj)
                            "
                          >
                            <v-tooltip bottom v-if="holidayDates[d.key]">
                              <template v-slot:activator="{ on, attrs }">
                                <v-chip v-bind="attrs" v-on="on" x-small color="teal" text-color="white">H</v-chip>
                              </template>
                              <span>{{ holidayDates[d.key] }}</span>
                            </v-tooltip>
                            <span v-else>{{ d.dayOfWeek === 0 ? "W" : "A" }}</span>
                          </span>
                          <span v-else> - </span>
                        </span>
                      </td>
                      <td rowspan="3">{{ user.present_count }}</td>
                      <td rowspan="3">{{ user.absent_count }}</td>
                      <td rowspan="3">{{ user.leave_count }}</td>
                      <td rowspan="3">{{ user.meeting_count }}</td>
                      <td rowspan="3">{{ user.holiday_count || 0 }}</td>
                      <td rowspan="3">
                        <v-chip small :color="user.late_count >= 7 ? 'red' : (user.late_count >= 4 ? 'orange' : 'grey')" text-color="white">
                          {{ user.late_count || 0 }}
                        </v-chip>
                      </td>
                      <td rowspan="3">
                        <v-chip small :color="user.half_day_count > 0 ? 'amber darken-2' : 'grey'" text-color="white">
                          {{ user.half_day_count || 0 }}
                        </v-chip>
                      </td>
                      <td rowspan="3">
                        <v-chip small :color="getExcessBreakCount(user) > 0 ? 'red' : 'grey'" text-color="white">
                          {{ getExcessBreakCount(user) }}
                        </v-chip>
                      </td>
                      <td rowspan="3">
                        <v-chip small color="blue-grey lighten-4" text-color="blue-grey darken-3" label>
                          <v-icon small left color="blue-grey darken-2">mdi-clock-outline</v-icon>
                          {{ getTotalHoursForUser(user) }}
                        </v-chip>
                      </td>
                    </tr>

                    <!-- Punch In/Out Times row -->
                    <tr
                      :key="`user${i}-times`"
                      style="background-color: #f5f5f5"
                    >
                      <td
                        v-for="d in dateRange"
                        :key="`times-${i}-${d.key}`"
                        class="day-cell"
                        style="font-size: 11px; padding: 6px 14px; vertical-align: top; min-width: 186px;"
                      >
                        <div v-if="user.attendances[d.key] && user.attendances[d.key].length > 0">
                          <!-- Single Shift - Compact Display -->
                          <div v-if="user.attendances[d.key].length === 1">
                            <div style="display: flex; flex-direction: column; gap: 2px;">
                              <!-- Shift badge with icon -->
                              <div v-if="user.attendances[d.key][0].shift" style="margin-bottom: 3px;">
                                <v-chip x-small color="blue-grey" text-color="white" label>
                                  <v-icon x-small left>mdi-briefcase-clock</v-icon>
                                  {{ getShiftShortName(user.attendances[d.key][0].shift.shift_name) }}
                                </v-chip>
                              </div>

                              <!-- In/Out times on single line -->
                              <div style="display: flex; justify-content: space-between; align-items: center;">
                                <span style="color: #2e7d32; font-weight: 600;">
                                  <v-icon x-small color="green">mdi-login</v-icon>
                                  {{ formatTime(user.attendances[d.key][0].login_time) }}
                                </span>
                                <v-icon x-small>mdi-arrow-right</v-icon>
                                <span style="color: #c62828; font-weight: 600;">
                                  <v-icon x-small color="red">mdi-logout</v-icon>
                                  {{ formatTime(user.attendances[d.key][0].logout_time) }}
                                  <v-chip v-if="isNextDay(user.attendances[d.key][0].login_date, user.attendances[d.key][0].logout_date)" x-small color="orange" text-color="white" class="ml-1">+1d</v-chip>
                                </span>
                              </div>

                              <!-- Status info if late/half/absent -->
                              <div v-if="user.attendances[d.key][0].attendance_status && user.attendances[d.key][0].attendance_status !== 'present'" style="margin-top: 3px;">
                                <v-chip x-small :color="getStatusColor(user.attendances[d.key][0].attendance_status)" text-color="white" label>
                                  <v-icon x-small left>{{ getStatusIcon(user.attendances[d.key][0].attendance_status) }}</v-icon>
                                  {{ getStatusLabel(user.attendances[d.key][0].attendance_status) }}
                                  <span v-if="user.attendances[d.key][0].is_late"> - {{ user.attendances[d.key][0].late_minutes }}m</span>
                                </v-chip>
                              </div>
                            </div>
                          </div>

                          <!-- Multiple Shifts - Clear Separation -->
                          <div v-else>
                            <div
                              v-for="(att, idx) in user.attendances[d.key]"
                              :key="`times-${d.key}-${idx}`"
                              style="margin-bottom: 8px; padding: 6px; background: white; border-radius: 4px; border-left: 3px solid"
                              :style="`border-left-color: ${idx === 0 ? '#FF9800' : '#3F51B5'}`"
                            >
                              <!-- Shift header -->
                              <div style="display: flex; align-items: center; margin-bottom: 4px; font-weight: bold; font-size: 10px;">
                                <v-icon x-small :color="idx === 0 ? 'orange' : 'indigo'" style="margin-right: 4px;">
                                  {{ idx === 0 ? 'mdi-weather-sunny' : 'mdi-weather-night' }}
                                </v-icon>
                                <span :style="`color: ${idx === 0 ? '#FF9800' : '#3F51B5'}`">
                                  {{ att.shift ? getShiftShortName(att.shift.shift_name) : (idx === 0 ? 'Shift 1' : 'Shift 2') }}
                                </span>
                              </div>

                              <!-- Time row -->
                              <div style="display: flex; gap: 8px; font-size: 10px; margin-bottom: 2px;">
                                <span style="color: #2e7d32;">
                                  <v-icon x-small color="green">mdi-login</v-icon>
                                  {{ formatTime(att.login_time) }}
                                </span>
                                <v-icon x-small>mdi-arrow-right</v-icon>
                                <span style="color: #c62828;">
                                  <v-icon x-small color="red">mdi-logout</v-icon>
                                  {{ formatTime(att.logout_time) }}
                                  <v-chip v-if="isNextDay(att.login_date, att.logout_date)" x-small color="orange" text-color="white" class="ml-1">+1d</v-chip>
                                </span>
                              </div>

                              <!-- Status/Late info -->
                              <div v-if="att.attendance_status && att.attendance_status !== 'present'" style="margin-top: 3px;">
                                <v-chip x-small :color="getStatusColor(att.attendance_status)" text-color="white" label>
                                  {{ getStatusLabel(att.attendance_status) }}
                                  <span v-if="att.is_late"> - {{ att.late_minutes }}m</span>
                                </v-chip>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else style="text-align: center; color: #bbb;">-</div>
                      </td>
                    </tr>

                    <!-- Total Hours row -->
                    <tr
                      :key="`user${i}-hours`"
                      style="background-color: #e3f2fd; border-bottom: 3px solid #90a4ae;"
                    >
                      <td
                        v-for="d in dateRange"
                        :key="`hours-${i}-${d.key}`"
                        class="day-cell"
                        style="
                          font-size: 11px;
                          text-align: center;
                          font-weight: bold;
                          vertical-align: top;
                          padding: 8px 4px;
                          min-width: 80px;
                        "
                      >
                        <div v-if="user.attendances[d.key] && user.attendances[d.key].length > 0">
                          <!-- Single shift -->
                          <div v-if="user.attendances[d.key].length === 1">
                            <v-chip small color="blue-grey lighten-4" text-color="blue-grey darken-3" label>
                              <v-icon small left color="blue-grey darken-2">mdi-clock-outline</v-icon>
                              {{
                                calculateTotalHours(
                                  user.attendances[d.key][0].login_time,
                                  user.attendances[d.key][0].logout_time,
                                  user.attendances[d.key][0].login_date || user.attendances[d.key][0].date,
                                  user.attendances[d.key][0].logout_date || user.attendances[d.key][0].login_date || user.attendances[d.key][0].date
                                )
                              }}
                            </v-chip>
                          </div>

                          <!-- Multiple shifts -->
                          <div v-else style="display: flex; flex-direction: column; gap: 4px; align-items: center;">
                            <div
                              v-for="(att, idx) in user.attendances[d.key]"
                              :key="`hours-${d.key}-${idx}`"
                            >
                              <v-chip x-small :color="idx === 0 ? 'orange lighten-4' : 'indigo lighten-4'" :text-color="idx === 0 ? 'orange darken-3' : 'indigo darken-3'" label>
                                <v-icon x-small left>{{ idx === 0 ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
                                {{
                                  calculateTotalHours(
                                    att.login_time,
                                    att.logout_time,
                                    att.login_date || att.date,
                                    att.logout_date || att.login_date || att.date
                                  )
                                }}
                              </v-chip>
                            </div>
                            <!-- Total combined hours -->
                            <v-chip x-small color="green lighten-4" text-color="green darken-3" label style="margin-top: 2px;">
                              <v-icon x-small left>mdi-sigma</v-icon>
                              {{ calculateCombinedHours(user.attendances[d.key]) }}
                            </v-chip>
                          </div>
                        </div>
                        <span v-else style="color: #bbb;">-</span>
                      </td>
                    </tr>
                  </template>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td
                      :colspan="`${8 + dateRange.length}`"
                      class="text-center"
                    >
                      No data found
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import moment from "moment";
export default {
  name: "monthlyAttendancesPage",
  data() {
    return {
      filter: {
        start_date: "",
        end_date: "",
        user_id: "",
        role: "",
        plant_id: "",
        salary_cycle_id: "",
        cycle_month: "",
        filter_company_id: "",
      },
      companies: [],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      search_keyword: "",
      supervisor_id: "",
      dateRange: [],

      start_date_menu: false,
      end_date_menu: false,
      cycle_month_menu: false,
      employees: [
        {
          id: 0,
          text: "Select Employee",
          value: "",
        },
      ],
      roles: [
        {
          text: "USER",
          value: "USER",
        },
        {
          text: "SUPERVISOR",
          value: "SUPERVISOR",
        },
      ],
      plants: [
        {
          id: 0,
          text: "Select Plant",
          value: "",
        },
      ],
      salaryCycles: [
        {
          id: 0,
          text: "Select Salary Cycle",
          value: "",
        },
      ],
      salaryCycleMap: {},
      holidayDates: {},

      isLoading: false,
      isExcelLoading: false,
      isExcelDownloaded: false,
      monthlyAttendances: [],
      ExportMonthlyAttendances: [],
      All_monthlyAttendances: [],
      excel_fields: {
        "Sr No": "sr_no",
        Employee_code: "Employee_code",
        Name: "name",
        Supervisor: "supervisor",
        Date_of_Joining: "Date_of_Joining",
        designation: "designation",
      },
    };
  },
  watch: {
    page: "getData",
  },

  mounted() {
    if (this.user.roles[0].name == "SUPERVISOR") {
      this.supervisor_id = this.user.id;
    }
    // Set default start_date to 1st of current month and end_date to today
    const currentDate = moment();
    this.filter.start_date = currentDate.clone().startOf("month").format("YYYY-MM-DD");
    this.filter.end_date = currentDate.format("YYYY-MM-DD");
    this.buildDateRange();

    this.getMasters();
    this.getData();
  },
  methods: {
    //    isThirdOrFourthSaturday(year, month, day) {
    //   const date = new Date(year, month - 1, day);
    //   const dayOfWeek = date.getDay();
    //   const weekOfMonth = Math.ceil((date.getDate() - dayOfWeek) / 7);

    //   // Check if it's the 3rd or 4th Saturday (dayOfWeek === 6 for Saturday)
    //   return dayOfWeek === 6 && (weekOfMonth === 3 || weekOfMonth === 4);
    // },

    async onCompanyChange() {
      // Clear salary cycle when company changes
      this.filter.salary_cycle_id = "";
      this.filter.cycle_month = "";
      // Reload salary cycles for selected company
      await this.loadSalaryCycles();
      this.getData();
    },
    async loadSalaryCycles() {
      let companyParam = this.filter.filter_company_id || "";
      let response = await axios.get(`user_attendances/masters?filter_company_id=${companyParam}`);
      let data = response.data;
      this.salaryCycles = [
        { id: 0, text: "Select Salary Cycle", value: "" },
      ];
      if (data.salaryCycles) {
        data.salaryCycles.forEach((cycle) => {
          this.salaryCycles.push({
            id: cycle.id,
            text: cycle.name,
            value: cycle.id,
          });
          this.salaryCycleMap[cycle.id] = {
            start_day: cycle.start_day,
            end_day: cycle.end_day,
          };
        });
      }
    },
    async getMasters() {
      let masters = await axios.get(`user_attendances/masters`);
      masters = masters.data;
      // Employees
      masters.employees.forEach((employee) => {
        if (this.user.roles[0].name == "SUPERVISOR") {
          if (employee.supervisor_id == this.user.id) {
            this.employees.push({
              id: employee.id,
              text: employee.name,
              value: employee.id,
            });
          }
        } else {
          this.employees.push({
            id: employee.id,
            text: employee.name,
            value: employee.id,
          });
        }
      });
      // Plants
      if (masters.plants) {
        masters.plants.forEach((plant) => {
          this.plants.push({
            id: plant.id,
            text: plant.name || plant.description,
            value: plant.id,
          });
        });
      }
      // Salary Cycles
      if (masters.salaryCycles) {
        masters.salaryCycles.forEach((cycle) => {
          this.salaryCycles.push({
            id: cycle.id,
            text: cycle.name,
            value: cycle.id,
          });
          this.salaryCycleMap[cycle.id] = {
            start_day: cycle.start_day,
            end_day: cycle.end_day,
          };
        });
      }
      // Companies (only for main company)
      if (masters.companies && masters.companies.length > 0) {
        this.companies = [
          { text: "All Companies", value: "all" },
        ];
        masters.companies.forEach((company) => {
          this.companies.push({
            text: company.name,
            value: company.id,
          });
        });
      }
      // Holidays
      if (masters.holidays) {
        this.holidayDates = {};
        masters.holidays.forEach((h) => {
          const dateKey = h.date ? h.date.substring(0, 10) : "";
          if (dateKey) {
            this.holidayDates[dateKey] = h.name;
          }
        });
      }
    },
    onSalaryCycleChange() {
      if (this.filter.salary_cycle_id) {
        const cycle = this.salaryCycleMap[this.filter.salary_cycle_id];
        if (cycle) {
          // Set default cycle_month to current month if not set
          if (!this.filter.cycle_month) {
            this.filter.cycle_month = moment().format("YYYY-MM");
          }

          const ref = moment(this.filter.cycle_month, "YYYY-MM");
          const refMonth = ref.month(); // 0-indexed
          const refYear = ref.year();
          const today = moment();

          // Cycle runs: start_day of previous month -> end_day of selected month
          const prev = moment({ year: refYear, month: refMonth }).subtract(1, "month");
          const daysInPrev = prev.daysInMonth();
          const daysInCurrent = moment({ year: refYear, month: refMonth }).daysInMonth();

          const startDay = Math.min(cycle.start_day, daysInPrev);
          const endDay = Math.min(cycle.end_day, daysInCurrent);

          const startDate = moment({ year: prev.year(), month: prev.month(), day: startDay });
          let endDate = moment({ year: refYear, month: refMonth, day: endDay });

          // Cap end date to today if it's in the future
          if (endDate.isAfter(today)) {
            endDate = today;
          }

          this.filter.start_date = startDate.format("YYYY-MM-DD");
          this.filter.end_date = endDate.format("YYYY-MM-DD");
        }
      } else {
        // Salary cycle cleared - reset cycle_month
        this.filter.cycle_month = "";
      }
      this.page = 1;
      this.getData();
    },
    async getData() {
      this.isLoading = true;
      this.buildDateRange();
      this.search_keyword = this.search_keyword ? this.search_keyword : "";
      this.filter.user_id = this.filter.user_id ? this.filter.user_id : "";
      this.filter.role = this.filter.role ? this.filter.role : "";
      this.filter.plant_id = this.filter.plant_id ? this.filter.plant_id : "";
      this.filter.salary_cycle_id = this.filter.salary_cycle_id ? this.filter.salary_cycle_id : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&search_keyword=" +
        this.search_keyword +
        "&start_date=" +
        (this.filter.start_date || "") +
        "&end_date=" +
        (this.filter.end_date || "") +
        "&user_id=" +
        this.filter.user_id +
        "&role=" +
        this.filter.role +
        "&plantId=" +
        this.filter.plant_id +
        "&salary_cycle_id=" +
        this.filter.salary_cycle_id +
        "&filter_company_id=" +
        (this.filter.filter_company_id || "");
      await axios
        .get(`monthly_attendances?${query}`)
        .then((monthlyAttendances) => {
          this.ExportMonthlyAttendances = [];
          this.isExcelDownloaded = false;
          this.monthlyAttendances = monthlyAttendances.data.data;
          this.count = monthlyAttendances.data.count;
          // Update holiday dates from response
          if (monthlyAttendances.data.holidays) {
            this.holidayDates = monthlyAttendances.data.holidays;
          }
          // Update dates if salary cycle resolved them differently
          if (monthlyAttendances.data.resolved_start_date && this.filter.salary_cycle_id) {
            this.filter.start_date = monthlyAttendances.data.resolved_start_date;
            this.filter.end_date = monthlyAttendances.data.resolved_end_date;
            this.buildDateRange();
          }
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },

    async addToExcelData() {
      this.All_monthlyAttendances = [];
      this.ExportMonthlyAttendances = [];
      this.isExcelLoading = true;
      this.buildDateRange();
      this.title =
        "Monthly Attendance | " + (this.filter.start_date || "") + " to " + (this.filter.end_date || "");
      // Reset excel fields to base fields and rebuild for current date range
      this.excel_fields = {
        "Sr No": "sr_no",
        Employee_code: "Employee_code",
        Name: "name",
        Supervisor: "supervisor",
        Date_of_Joining: "Date_of_Joining",
        designation: "designation",
      };
      // Build excel fields for each date in range
      this.dateRange.forEach((d) => {
        this.excel_fields[d.label] = d.key;
      });
      this.excel_fields["Present Count"] = "present_count";
      this.excel_fields["Absent Count"] = "absent_count";
      this.excel_fields["Leave Count"] = "leave_count";
      this.excel_fields["Meeting Count"] = "meeting_count";
      this.excel_fields["Holiday Count"] = "holiday_count";
      this.excel_fields["Late Mark"] = "late_count";
      this.excel_fields["Half Day"] = "half_day_count";
      this.excel_fields["Excess Break"] = "excess_break_count";
      this.excel_fields["Total Hours"] = "total_hours";
      this.search_keyword = this.search_keyword ? this.search_keyword : "";
      this.filter.user_id = this.filter.user_id ? this.filter.user_id : "";
      this.filter.role = this.filter.role ? this.filter.role : "";
      this.filter.salary_cycle_id = this.filter.salary_cycle_id ? this.filter.salary_cycle_id : "";
      let query =
        "search_keyword=" +
        this.search_keyword +
        "&start_date=" +
        (this.filter.start_date || "") +
        "&end_date=" +
        (this.filter.end_date || "") +
        "&user_id=" +
        this.filter.user_id +
        "&role=" +
        this.filter.role +
        "&salary_cycle_id=" +
        this.filter.salary_cycle_id +
        "&filter_company_id=" +
        (this.filter.filter_company_id || "");
      await axios
        .get(`monthly_attendances?${query}`)
        .then((response) => {
          this.All_monthlyAttendances = response.data.data;
          if (response.data.holidays) {
            this.holidayDates = response.data.holidays;
          }
          let sr_no = 1;
          this.All_monthlyAttendances.forEach((user) => {
            // Row 1: Attendance Status Row
            let row1_attendance = {
              sr_no: sr_no++,
              Employee_code: user.employee_code
                ? user.employee_code.toUpperCase()
                : "",
              name: user.name ? user.name.toUpperCase() : "",
              supervisor:
                user.supervisor_id && user.supervisor
                  ? user.supervisor.name.toUpperCase()
                  : "",
              Date_of_Joining: user.doj ? user.doj : "",
              designation:
                user.designation_id && user.designation
                  ? user.designation.code.toUpperCase()
                  : "",
              present_count: user.present_count,
              absent_count: user.absent_count,
              leave_count: user.leave_count,
              meeting_count: user.meeting_count,
              holiday_count: user.holiday_count || 0,
              late_count: user.late_count || 0,
              half_day_count: user.half_day_count || 0,
              excess_break_count: this.getExcessBreakCount(user),
              total_hours: this.getTotalHoursForUser(user),
            };

            // Row 2: Punch In/Out Times Row
            let row2_times = {
              sr_no: "",
              Employee_code: "",
              name: "In/Out Times",
              supervisor: "",
              Date_of_Joining: "",
              designation: "",
              present_count: "",
              absent_count: "",
              leave_count: "",
              meeting_count: "",
              holiday_count: "",
              late_count: "",
              half_day_count: "",
              excess_break_count: "",
              total_hours: "",
            };

            // Row 3: Total Hours Row
            let row3_hours = {
              sr_no: "",
              Employee_code: "",
              name: "Total Hours",
              supervisor: "",
              Date_of_Joining: "",
              designation: "",
              present_count: "",
              absent_count: "",
              leave_count: "",
              meeting_count: "",
              holiday_count: "",
              late_count: "",
              half_day_count: "",
              excess_break_count: "",
              total_hours: "",
            };

            // Populate day columns for all 3 rows
            this.dateRange.forEach((d) => {
              let dateKey = d.key;
              if (
                user.attendances[dateKey] &&
                user.attendances[dateKey].length > 0
              ) {
                // Handle multiple shifts per day
                let statusText = "";
                let timesText = "";
                let hoursText = "";

                user.attendances[dateKey].forEach((att, idx) => {
                  // Determine shift name for multiple shifts
                  let shiftLabel = "";
                  if (user.attendances[dateKey].length > 1) {
                    if (att.shift && att.shift.shift_name) {
                      shiftLabel = this.getShiftShortName(att.shift.shift_name) + ": ";
                    } else {
                      shiftLabel = (idx === 0 ? "Shift 1: " : "Shift 2: ");
                    }
                  }

                  // Row 1: Attendance status
                  const status = att.session_type
                    ? att.session_type.description.charAt(0).toUpperCase()
                    : "A";
                  const lateInfo = att.is_late ? " (Late)" : "";
                  statusText += `${shiftLabel}${status}${lateInfo}`;
                  if (idx < user.attendances[dateKey].length - 1) statusText += "\n";

                  // Row 2: Punch in/out times in 12-hour format
                  const loginTime = this.formatTime(att.login_time);
                  const logoutTime = this.formatTime(att.logout_time);
                  const nextDayTag = this.isNextDay(att.login_date, att.logout_date) ? " (+1d)" : "";
                  timesText += `${shiftLabel}${loginTime} to ${logoutTime}${nextDayTag}`;
                  if (idx < user.attendances[dateKey].length - 1) timesText += "\n";

                  // Row 3: Total hours per shift
                  const aLoginDate = att.login_date || att.date;
                  const aLogoutDate = att.logout_date || aLoginDate;
                  const hours = this.calculateTotalHours(att.login_time, att.logout_time, aLoginDate, aLogoutDate);
                  hoursText += `${shiftLabel}${hours}`;
                  if (idx < user.attendances[dateKey].length - 1) hoursText += "\n";
                });

                // Add total hours for multiple shifts
                if (user.attendances[dateKey].length > 1) {
                  const totalHours = this.calculateCombinedHours(user.attendances[dateKey]);
                  hoursText += `\nTotal: ${totalHours}`;
                }

                row1_attendance[dateKey] = statusText;
                row2_times[dateKey] = timesText;
                row3_hours[dateKey] = hoursText;
              } else {
                // Check if it's a holiday, Sunday, or absent
                if (this.holidayDates[dateKey]) {
                  row1_attendance[dateKey] = "H";
                } else if (d.dayOfWeek === 0) {
                  row1_attendance[dateKey] = "W";
                } else {
                  row1_attendance[dateKey] = "A";
                }
                row2_times[dateKey] = "-";
                row3_hours[dateKey] = "-";
              }
            });

            // Add all 3 rows to export data
            this.ExportMonthlyAttendances.push(row1_attendance);
            this.ExportMonthlyAttendances.push(row2_times);
            this.ExportMonthlyAttendances.push(row3_hours);
          });
          this.isExcelLoading = false;
          this.isExcelDownloaded = true;
          // Auto-trigger download after data is ready
          this.$nextTick(() => {
            if (this.$refs.excelDownload && this.$refs.excelDownload.$el) {
              this.$refs.excelDownload.$el.click();
            }
          });
        })
        .catch(() => {
          this.isExcelLoading = false;
        });
    },
    buildDateRange() {
      this.dateRange = [];
      if (!this.filter.start_date || !this.filter.end_date) return;

      let start = moment(this.filter.start_date);
      let end = moment(this.filter.end_date);
      let isSameMonth = start.month() === end.month() && start.year() === end.year();

      while (start.isSameOrBefore(end)) {
        this.dateRange.push({
          key: start.format("YYYY-MM-DD"),
          label: isSameMonth ? start.format("D") : start.format("D MMM"),
          dayOfWeek: start.day(),
        });
        start.add(1, "day");
      }
    },

    formatTime(timestamp) {
      if (!timestamp) return "-";
      // Check if it's already in HH:mm format
      if (
        typeof timestamp === "string" &&
        /^\d{2}:\d{2}(:\d{2})?$/.test(timestamp)
      ) {
        // Parse as HH:mm:ss and format to 12-hour with AM/PM
        return moment(timestamp, "HH:mm:ss").format("hh:mm A");
      }
      // It's a timestamp, format to 12-hour with AM/PM
      return moment(timestamp).format("hh:mm A");
    },

    calculateTotalHours(loginTime, logoutTime, loginDate, logoutDate) {
      if (!loginTime || !logoutTime) return "-";

      let start, end;

      // Preferred: build full datetime from explicit dates + HH:MM times
      if (loginDate && logoutDate && typeof loginTime === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(loginTime)) {
        start = moment(loginDate + " " + loginTime, "YYYY-MM-DD HH:mm:ss");
        end   = moment(logoutDate + " " + logoutTime, "YYYY-MM-DD HH:mm:ss");
      }
      // HH:MM or HH:MM:SS without dates — same-day assumption
      else if (typeof loginTime === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(loginTime)) {
        start = moment(loginTime, "HH:mm:ss");
        end = moment(logoutTime, "HH:mm:ss");
      }
      // Legacy timestamp format
      else {
        start = moment(loginTime);
        end = moment(logoutTime);
      }

      if (!start.isValid() || !end.isValid()) return "-";

      const duration = moment.duration(end.diff(start));
      const totalMinutes = Math.floor(duration.asMinutes());
      if (totalMinutes < 0) return "-";

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    },

    isNextDay(loginDate, logoutDate) {
      if (!loginDate || !logoutDate) return false;
      return loginDate !== logoutDate;
    },

    getStatusColor(status) {
      const statusColors = {
        present: "green",
        late: "orange",
        half_day: "deep-orange",
        absent: "red",
      };
      return statusColors[status] || "grey";
    },

    getStatusLabel(status) {
      const statusLabels = {
        present: "Present",
        late: "Late",
        half_day: "Half Day",
        absent: "Absent (7+ Late)", // Penalty for 7+ late marks in the month
      };
      return statusLabels[status] || status;
    },

    getShiftShortName(shiftName) {
      if (!shiftName) return "";
      // Shorten shift names like "Morning Shift" -> "Morning"
      return shiftName.replace(/\s+Shift$/i, "").trim();
    },

    getStatusIcon(status) {
      const statusIcons = {
        present: "mdi-check-circle",
        late: "mdi-clock-alert",
        half_day: "mdi-clock-minus",
        absent: "mdi-close-circle",
      };
      return statusIcons[status] || "mdi-information";
    },

    getExcessBreakCount(user) {
      if (!user.attendances) return 0;
      let count = 0;
      for (let day in user.attendances) {
        let dayData = user.attendances[day];
        let records = Array.isArray(dayData) ? dayData : [dayData];
        for (let att of records) {
          if (att && att.break_start_time && att.break_end_time && att.shift && att.shift.break_start_time && att.shift.break_end_time) {
            let actualMinutes = moment(att.break_end_time).diff(moment(att.break_start_time), 'minutes', true);
            let allowedMinutes = moment(att.shift.break_end_time, 'HH:mm:ss').diff(moment(att.shift.break_start_time, 'HH:mm:ss'), 'minutes', true);
            if (actualMinutes > allowedMinutes) {
              count++;
            }
          }
        }
      }
      return count;
    },
    getTotalHoursForUser(user) {
      if (!user || !user.attendances) return "0h 0m";
      let totalMinutes = 0;
      Object.keys(user.attendances).forEach((dateKey) => {
        const dayAttendances = user.attendances[dateKey];
        if (!dayAttendances || !dayAttendances.length) return;
        dayAttendances.forEach((att) => {
          if (!att.login_time || !att.logout_time) return;
          const loginDate = att.login_date || att.date;
          const logoutDate = att.logout_date || loginDate;
          let start, end;
          if (loginDate && logoutDate && typeof att.login_time === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(att.login_time)) {
            start = moment(loginDate + " " + att.login_time, "YYYY-MM-DD HH:mm:ss");
            end = moment(logoutDate + " " + att.logout_time, "YYYY-MM-DD HH:mm:ss");
          } else if (typeof att.login_time === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(att.login_time)) {
            start = moment(att.login_time, "HH:mm:ss");
            end = moment(att.logout_time, "HH:mm:ss");
          } else {
            start = moment(att.login_time);
            end = moment(att.logout_time);
          }
          if (start.isValid() && end.isValid()) {
            const minutes = Math.floor(moment.duration(end.diff(start)).asMinutes());
            if (minutes > 0) totalMinutes += minutes;
          }
        });
      });
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h ${minutes}m`;
    },
    calculateCombinedHours(attendances) {
      if (!attendances || attendances.length === 0) return "-";

      let totalMinutes = 0;

      attendances.forEach(att => {
        if (!att.login_time || !att.logout_time) return;

        let start, end;
        const loginDate = att.login_date || att.date;
        const logoutDate = att.logout_date || loginDate;

        if (loginDate && logoutDate && typeof att.login_time === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(att.login_time)) {
          start = moment(loginDate + " " + att.login_time, "YYYY-MM-DD HH:mm:ss");
          end   = moment(logoutDate + " " + att.logout_time, "YYYY-MM-DD HH:mm:ss");
        } else if (typeof att.login_time === "string" && /^\d{2}:\d{2}(:\d{2})?$/.test(att.login_time)) {
          start = moment(att.login_time, "HH:mm:ss");
          end = moment(att.logout_time, "HH:mm:ss");
        } else {
          start = moment(att.login_time);
          end = moment(att.logout_time);
        }

        if (start.isValid() && end.isValid()) {
          const duration = moment.duration(end.diff(start));
          const minutes = Math.floor(duration.asMinutes());
          if (minutes >= 0) {
            totalMinutes += minutes;
          }
        }
      });

      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours}h ${minutes}m`;
    },
  },
};
</script>

<style scoped>
/* Vuetify v-simple-table fixed header styling */
.monthly-attendance-table >>> .v-data-table__wrapper {
  overflow: auto;
}

.monthly-attendance-table >>> thead th {
  position: sticky !important;
  top: 0 !important;
  z-index: 3 !important;
  background-color: #f5f5f5 !important;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Top border on status row to separate each employee */
.monthly-attendance-table >>> tbody tr.emp-first-row td {
  border-top: 1px solid #78909c !important;
}

/* Vertical borders on date columns only */
.monthly-attendance-table >>> tbody td.day-cell {
  border-left: 1px solid #e0e0e0 !important;
  border-right: 1px solid #e0e0e0 !important;
}

/* Scrollbar styling */
.monthly-attendance-table >>> .v-data-table__wrapper::-webkit-scrollbar {
  height: 14px;
  width: 14px;
}

.monthly-attendance-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
  background: #e0e0e0;
  border-radius: 7px;
}

.monthly-attendance-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: 7px;
  border: 2px solid #e0e0e0;
}

.monthly-attendance-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
  background-color: #555;
}

/* Scrollbar corner */
.monthly-attendance-table >>> .v-data-table__wrapper::-webkit-scrollbar-corner {
  background: #e0e0e0;
}
</style>
